// src/components/ShopList.js - Updated with better error handling and display
import React from 'react';
import { Link } from 'react-router-dom';
import { List, Card, Image, Tag, Rate, Button, Typography, Space, Tooltip, Spin, Alert } from 'antd';
import { EnvironmentOutlined, InfoCircleOutlined, ClockCircleOutlined, CheckCircleOutlined, StarFilled } from '@ant-design/icons';

const { Meta } = Card;
const { Text, Paragraph } = Typography;

// Helper functions (keep them as they are useful)
const formatOpeningHours = (openingHours) => {
    if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
        return 'Keine Angaben';
    }
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = days[new Date().getDay()];
    const todayHours = openingHours.find(day => day.dayOfWeek === today);

    if (!todayHours) return 'Heute keine Angaben';
    if (!todayHours.opens || !todayHours.closes) return 'Heute geschlossen';
    if (todayHours.opens === '00:00' && todayHours.closes === '24:00') return '24 Stunden geöffnet';

    return `${todayHours.opens} - ${todayHours.closes} Uhr`;
};

const isShopOpen = (openingHours) => {
    if (!openingHours || !Array.isArray(openingHours)) return false;
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = days[new Date().getDay()];
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const todayHours = openingHours.find(day => day.dayOfWeek === today);
    if (!todayHours || !todayHours.opens || !todayHours.closes) return false;
    if (todayHours.opens === '00:00' && todayHours.closes === '24:00') return true;
    return currentTime >= todayHours.opens && currentTime <= todayHours.closes;
};

const ShopList = ({ shops }) => { // Removed location prop

    // Basic check if shops prop is valid (though parent should handle loading/empty)
    if (!Array.isArray(shops)) {
        console.error('ShopList: Invalid shops prop received.');
        return null; // Render nothing if prop is invalid
    }

    return (
        <List
            grid={{
                gutter: 16, // Space between cards
                xs: 1, // 1 column on extra small screens
                sm: 1, // 1 column on small screens
                md: 2, // 2 columns on medium screens
                lg: 3, // 3 columns on large screens
                xl: 3, // 3 columns on extra large screens
                xxl: 4, // 4 columns on extra extra large screens
            }}
            dataSource={shops}
            renderItem={shop => {
                // DEBUG: Log shop data
                console.log('ShopList:', shop?.name, 'Plan:', shop?.subscriptionPlan, 'Verified:', shop?.isVerified);

                if (!shop || typeof shop !== 'object' || !shop.id) {
                    console.error('Invalid shop data in list:', shop);
                    return null; // Skip rendering invalid item
                }

                const isOpen = isShopOpen(shop.openingHours);
                const currentHours = formatOpeningHours(shop.openingHours);
                const shopLink = `/shops/${shop.id}`;
                const mapsLink = shop.coordinates
                    ? `https://www.google.com/maps/dir/?api=1&destination=${shop.coordinates.lat},${shop.coordinates.lng}`
                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shop.name + ", " + shop.address + ", " + shop.city)}`;

                // NEU: Hintergrund-Style basierend auf Plan
                const listItemStyle = {
                    // Hintergrund-Logik hier im List.Item
                    backgroundColor: shop.subscriptionPlan === 'professional' ? '#fffbe6' : 'transparent', // Antd gold-1
                    borderRadius: '8px', // Optional: Radius beibehalten
                    padding: '1px', // War für den Rahmen, evtl. entfernen oder anpassen
                    height: '100%' // Wichtig, damit das Item die Höhe bestimmt
                };

                return (
                    <List.Item key={shop.id} style={listItemStyle}>
                        <Card
                            hoverable
                            style={{
                                // Kein Rahmen mehr
                                border: 'none',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                            cover={
                                <Link to={shopLink}>
                                    <div style={{ height: '200px', overflow: 'hidden' }}>
                                        {/* Ensure shop exists BEFORE checking images. Simplified condition. */}
                                        {shop && shop.images && shop.images.length > 0 && shop.images[0]?.url ? (
                                            // Render actual shop image if available
                                            <Image
                                                alt={shop.name}
                                                src={shop.images[0].url}
                                                fallback='/images/placeholder-shop.svg'
                                                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                                                preview={false}
                                            />
                                        ) : (
                                            // Back to normal placeholder Image component
                                            <Image
                                                alt="Kein Bild verfügbar"
                                                src="/images/placeholder-shop.svg"
                                                style={{ objectFit: 'contain', background: '#f0f0f0', width: '100%', height: '100%' }}
                                                preview={false}
                                            />
                                        )}
                                    </div>
                                </Link>
                            }
                            actions={[
                                <Tooltip title="Details ansehen">
                                    <Link to={shopLink}>
                                        <Button type="text" icon={<InfoCircleOutlined />} />
                                    </Link>
                                </Tooltip>,
                                <Tooltip title="Route berechnen (Google Maps)">
                                    <a href={mapsLink} target="_blank" rel="noopener noreferrer">
                                        <Button type="text" icon={<EnvironmentOutlined />} />
                                    </a>
                                </Tooltip>
                            ]}
                        >
                            <div style={{ minHeight: '240px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div>
                                    <Meta
                                        title={
                                            <Space direction="vertical" size={0}>
                                                <Link to={shopLink} style={{ color: 'inherit' }}>
                                                     {/* Titel: Fett für Professional */}
                                                     <Text strong={shop.subscriptionPlan === 'professional'} ellipsis={{ tooltip: shop.name }}>{shop.name}</Text>
                                                </Link>
                                            </Space>
                                        }
                                        description={
                                             <Paragraph ellipsis={{ rows: 2, tooltip: `${shop.address}, ${shop.postalCode} ${shop.city}` }} style={{ marginBottom: '8px' }}>
                                                 {shop.address}, {shop.postalCode} {shop.city}
                                             </Paragraph>
                                        }
                                    />
                                    <div style={{ marginTop: '8px', marginBottom: '8px', minHeight: '22px' }}>
                                        <Space wrap size={[4, 4]}>
                                            {/* Verifiziert Badge für aktive Premium & Pro */}
                                            {(shop.subscriptionPlan === 'premium' || shop.subscriptionPlan === 'professional') && shop.isActive && (
                                                <Tag color="success" icon={<CheckCircleOutlined />}>Verifiziert</Tag>
                                            )}
                                            {/* Goldener Stern nur für aktive Professional */}
                                            {shop.subscriptionPlan === 'professional' && shop.isActive && (
                                                <StarFilled style={{ color: '#faad14', fontSize: '16px', verticalAlign: 'middle' }} />
                                            )}
                                            {isOpen && <Tag color="green" icon={<ClockCircleOutlined />}>Jetzt geöffnet</Tag>}
                                            {!isOpen && currentHours !== 'Heute keine Angaben' && currentHours !== 'Heute geschlossen' && <Tag icon={<ClockCircleOutlined />}>Geschlossen</Tag>}
                                        </Space>
                                    </div>
                                </div>
                                <div>
                                    <Space direction="vertical" style={{ width: '100%', marginTop: '10px' }}>
                                        <Space size="small">
                                            <Rate disabled allowHalf value={shop.rating || 0} style={{ fontSize: '16px' }} />
                                            <Text type="secondary">({(shop.rating || 0).toFixed(1)})</Text>
                                            <Text type="secondary">({shop.reviewCount || 0} Bewertungen)</Text>
                                        </Space>
                                        <Text>
                                            <ClockCircleOutlined style={{ marginRight: 8 }} />
                                            {currentHours}
                                        </Text>
                                        {shop.brands && shop.brands.length > 0 && (
                                            <div>
                                                <Text strong>Marken: </Text>
                                                <Space size={[0, 8]} wrap>
                                                     {shop.brands.slice(0, 3).map((brand, index) => (
                                                         <Tag key={index}>{brand}</Tag>
                                                     ))}
                                                     {shop.brands.length > 3 && (
                                                         <Tag>+{shop.brands.length - 3} weitere</Tag>
                                                     )}
                                                </Space>
                                            </div>
                                        )}
                                    </Space>
                                </div>
                            </div>
                        </Card>
                    </List.Item>
                );
            }}
        />
    );
};

export default ShopList;